import {
  TreeItem,
  TreeInformation,
  TreeItemRenderContext
} from 'react-complex-tree';
import { useMemo } from 'react';
import { Box, Theme, makeStyles } from '@material-ui/core';

const useStyles = makeStyles<
  Theme,
  {
    isSelected: boolean;
    background: string;
    color: string;
    overflown: boolean;
  }
>(() => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: 4,
    backgroundColor: ({ isSelected, background }) =>
      isSelected ? background : 'transparent',
    cursor: 'pointer',
    color: ({ isSelected, color }) => (isSelected ? color : '#000'),
    gap: 16,
    height: 34,
    minWidth: 80,
    // width: ({ overflown }) => (!overflown ? '100%' : 'fit-content'),
    borderRadius: 6,

    '&:hover': {
      backgroundColor: ({ isSelected, background }) =>
        isSelected ? background : '#f5f5f5',
      opacity: ({ isSelected }) => (isSelected ? 0.6 : 1)
    },

    '& div': {
      backgroundColor: 'transparent !important'
    },

    '& svg': {
      color: ({ isSelected, color }) => (isSelected ? color : '#696974'),

      '& path': {
        fill: ({
          isSelected,
          color
        }: {
          isSelected: boolean;
          color: string;
        }) => (isSelected ? color : '#696974')
      }
    }
  }
}));

interface TreeItemProps<T = any, C extends string = never> {
  item: TreeItem<T>;
  depth: number;
  children: React.ReactNode | null;
  title: React.ReactNode;
  arrow: React.ReactNode;
  context: TreeItemRenderContext<C>;
  info: TreeInformation;
  itemColor?: string;
  itemBackground?: string;
  disableActiveStyle?: boolean;
  onClick?: (item: T) => void;
}

const ReactTreeItem = <T,>(props: TreeItemProps<T>) => {
  // We use this logic to determine if the element is overflown and according to that to apply width
  // If element is overflown we had bug that action buttons were not visible
  const currId = props.item?.index?.toString();
  const element = document.getElementById(currId);
  const isElementOverflown = useMemo(
    () =>
      element?.scrollHeight > element?.clientHeight ||
      element?.scrollWidth > element?.clientWidth,
    [element]
  );

  const classes = useStyles({
    isSelected: props.context.isFocused && !props?.disableActiveStyle,
    background: props.itemBackground || '#2196F3',
    color: props.itemColor || '#fff',
    overflown: isElementOverflown
  });

  return (
    <li
      {...props.context.itemContainerWithChildrenProps}
      id={currId}
      style={{
        paddingLeft: `${props.depth * 4}px`,
        listStyleType: 'none'
      }}>
      <button
        {...props.context.itemContainerWithoutChildrenProps}
        {...props.context.interactiveElementProps}
        type="button"
        onClick={() => props.onClick?.(props.item?.data)}
        style={{
          width: '100%',
          background: props.context.isDraggingOver ? '#EDEFF0' : 'transparent',
          borderRadius: 6
        }}>
        <Box className={classes.root}>
          <Box flexShrink={0} width={11} onClick={e => e.stopPropagation()}>
            {props.arrow}
          </Box>
          {props.title}
        </Box>
      </button>
      {props.children}
    </li>
  );
};

export default ReactTreeItem;
